import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Brain, Lock, Sparkles, CloudRain, Activity, Moon, Utensils, 
  Lightbulb, Search, Gauge, AlertCircle, TrendingUp, TrendingDown,
  Heart, Info
} from 'lucide-react';
import { useMentalCorrelations } from '@/hooks/useMentalCorrelations';
import { useSubscription, PRODUCTS } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const COGNITIVE_METRICS = [
  { key: 'rumination_minutes', label: 'Rumination (minutes)', icon: Brain, color: 'hsl(var(--chart-1))' },
  { key: 'intrusive_thoughts_score', label: 'Intrusive Thoughts', icon: Lightbulb, color: 'hsl(var(--chart-2))' },
  { key: 'thought_looping_score', label: 'Thought Looping', icon: Activity, color: 'hsl(var(--chart-3))' },
  { key: 'brain_fog_score', label: 'Brain Fog', icon: CloudRain, color: 'hsl(var(--chart-4))' },
  { key: 'concentration_difficulty_score', label: 'Concentration Difficulty', icon: Search, color: 'hsl(var(--chart-5))' },
  { key: 'memory_lapses_score', label: 'Memory Lapses', icon: Brain, color: 'hsl(var(--chart-1))' },
  { key: 'word_finding_difficulty_score', label: 'Word-Finding Difficulty', icon: Search, color: 'hsl(var(--chart-2))' },
  { key: 'mental_fatigue_score', label: 'Mental Fatigue', icon: Gauge, color: 'hsl(var(--chart-3))' },
];

const DATE_RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

// Gentle, non-shaming reflections for each metric
const METRIC_REFLECTIONS: Record<string, string> = {
  rumination_minutes: "Rumination often decreases with meditation, movement, and good sleep. Notice what helps your mind settle - this is information, not a verdict.",
  intrusive_thoughts_score: "Intrusive thoughts are an understandable nervous system response to stress. They often soften with mindfulness and self-compassion over time.",
  thought_looping_score: "Thought looping can be gently interrupted by movement, breathwork, or changing your environment. Your brain is trying to solve something.",
  brain_fog_score: "Brain fog frequently connects to sleep quality, hydration, and food choices. When it's lower, things tend to feel a little more manageable.",
  concentration_difficulty_score: "Concentration varies with sleep, stress, and nervous system state. Be gentle with yourself on hard days - this isn't a personal failure.",
  memory_lapses_score: "Memory tends to improve with good sleep, lower stress, and consistent routines. This is rarely cause for concern.",
  word_finding_difficulty_score: "Word-finding difficulties often relate to fatigue and cognitive load. Rest helps - your words will come back.",
  mental_fatigue_score: "Mental fatigue responds to rest, movement balance, and nervous system regulation. It's a signal, not a flaw.",
};

// Generate gentle correlation language
function getCorrelationInsight(label: string, correlation: number | null): string {
  if (correlation === null) return '';
  
  const lowerLabel = label.toLowerCase();
  
  if (correlation > 0.5) {
    return `It looks like on days when ${lowerLabel} is high, your mood tends to feel heavier. This is a normal pattern - cognitive strain and emotional weight often travel together.`;
  } else if (correlation > 0.3) {
    return `There's a gentle connection between higher ${lowerLabel} and lower mood. When one shifts, the other often follows.`;
  } else if (correlation < -0.3) {
    return `Interestingly, your ${lowerLabel} doesn't follow the typical pattern with mood. Your cognitive experience may have different drivers worth exploring.`;
  } else {
    return `Your ${lowerLabel} shows a subtle relationship with mood. As more data accumulates, clearer patterns may emerge.`;
  }
}

interface TimeSeriesData {
  date: string;
  value: number | null;
  displayDate: string;
}

export function MentalPatternsExplorer() {
  const { data, isLoading, error } = useMentalCorrelations(60);
  const correlations = data?.correlations ?? [];
  const { productId, hasPremiumAccess } = useSubscription();
  const navigate = useNavigate();
  
  const [selectedMetric, setSelectedMetric] = useState<string>('brain_fog_score');
  const [dateRange, setDateRange] = useState<string>('30');
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [isLoadingTimeSeries, setIsLoadingTimeSeries] = useState(false);

  const isSanctuary = productId === PRODUCTS.MASTERY || hasPremiumAccess();

  // Fetch time series data for selected metric
  useEffect(() => {
    const fetchTimeSeries = async () => {
      setIsLoadingTimeSeries(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoadingTimeSeries(false);
        return;
      }

      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');
      
      const { data: logs, error: fetchError } = await supabase
        .from('emotional_symptom_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .order('date', { ascending: true });

      if (!fetchError && logs) {
        const formattedData = logs.map(row => {
          const metricValue = row[selectedMetric as keyof typeof row];
          return {
            date: row.date,
            value: typeof metricValue === 'number' ? metricValue : null,
            displayDate: format(new Date(row.date), 'MMM d')
          };
        });
        setTimeSeriesData(formattedData);
      } else {
        setTimeSeriesData([]);
      }
      
      setIsLoadingTimeSeries(false);
    };

    if (isSanctuary) {
      fetchTimeSeries();
    }
  }, [selectedMetric, dateRange, isSanctuary]);

  // Gate behind Sanctuary subscription
  if (!isSanctuary) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl">Mental & Cognitive Patterns</CardTitle>
          <CardDescription>
            Sanctuary members get access to deep cognitive correlations - connecting brain fog, rumination, and mental fatigue with sleep, food, and mindfulness practices.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Moon className="w-4 h-4" />
              <span>Sleep ↔ Brain Fog correlations</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Brain className="w-4 h-4" />
              <span>Meditation ↔ Rumination & Intrusive Thoughts</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Activity className="w-4 h-4" />
              <span>Movement ↔ Mental Fatigue tracking</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Utensils className="w-4 h-4" />
              <span>Food Quality ↔ Cognitive Clarity</span>
            </div>
          </div>
          <Button onClick={() => navigate('/subscription')} className="w-full">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Sanctuary
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentMetricInfo = COGNITIVE_METRICS.find(m => m.key === selectedMetric) || COGNITIVE_METRICS[0];
  const MetricIcon = currentMetricInfo.icon;
  const hasTimeSeriesData = timeSeriesData.some(d => d.value !== null);

  // Sort correlations by strength for display
  const sortedCorrelations = [...correlations]
    .filter(c => c.correlationWithMood !== null)
    .sort((a, b) => Math.abs(b.correlationWithMood ?? 0) - Math.abs(a.correlationWithMood ?? 0));

  return (
    <div className="space-y-4">
      {/* Intro Card */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-medium mb-1">Understanding Your Thinking Patterns</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This space explores your mental and cognitive load - patterns like rumination, intrusive thoughts, 
                brain fog, and mental fatigue. These experiences are <strong>understandable nervous system responses</strong>, 
                not personal failures. The goal here is curiosity and compassion, not blame. 
                Think of these patterns as information about what your mind needs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Mental & Cognitive Patterns
          </CardTitle>
          <CardDescription>
            Explore how your cognitive symptoms connect with lifestyle factors. Brain fog, rumination, 
            and mental fatigue often have clear - and gentle - patterns.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary Insight */}
          {data?.summaryInsight && (
            <div className="p-4 rounded-lg bg-muted/50 border border-primary/10">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-foreground">{data.summaryInsight}</p>
              </div>
            </div>
          )}

          {/* Metric & Date Range Selectors */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedMetric} onValueChange={setSelectedMetric}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select metric" />
              </SelectTrigger>
              <SelectContent>
                {COGNITIVE_METRICS.map((metric) => {
                  const Icon = metric.icon;
                  return (
                    <SelectItem key={metric.key} value={metric.key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {metric.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data sufficiency indicator */}
          {data && (
            <div className="flex items-center gap-2">
              <Badge 
                variant={data.dataSufficiency === 'good' ? 'default' : 
                        data.dataSufficiency === 'adequate' ? 'secondary' : 'outline'}
                className="text-xs"
              >
                {data.daysAnalyzed} days analysed
              </Badge>
              {data.dataSufficiency === 'insufficient' && (
                <span className="text-xs text-muted-foreground">
                  Keep logging - patterns will emerge
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Series Chart */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted">
                <MetricIcon className="w-5 h-5" style={{ color: currentMetricInfo.color }} />
              </div>
              <div>
                <CardTitle className="text-base">{currentMetricInfo.label} Over Time</CardTitle>
                <CardDescription className="text-xs">
                  {DATE_RANGES.find(r => r.value === dateRange)?.label}
                </CardDescription>
              </div>
            </div>
            {hasTimeSeriesData && (
              <Badge variant="secondary" className="text-xs">
                {timeSeriesData.filter(d => d.value !== null).length} entries
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingTimeSeries ? (
            <Skeleton className="h-48 w-full" />
          ) : hasTimeSeriesData ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    domain={[0, selectedMetric === 'rumination_minutes' ? 'auto' : 10]} 
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke={currentMetricInfo.color}
                    strokeWidth={2}
                    dot={{ fill: currentMetricInfo.color, strokeWidth: 0, r: 3 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center">
              <div className="text-center p-6 rounded-lg bg-muted/30">
                <CloudRain className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  No data for {currentMetricInfo.label.toLowerCase()} yet
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Log your cognitive symptoms to see patterns over time
                </p>
              </div>
            </div>
          )}
          
          {/* Metric-specific reflection */}
          {METRIC_REFLECTIONS[selectedMetric] && hasTimeSeriesData && (
            <p className="text-xs text-muted-foreground mt-3 italic leading-relaxed">
              {METRIC_REFLECTIONS[selectedMetric]}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Correlation Insights - Cognitive ↔ Mood Links */}
      {sortedCorrelations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="w-4 h-4 text-primary" />
              What's Connected to Your Mood
            </CardTitle>
            <CardDescription className="text-xs">
              These are gentle patterns - information to explore with curiosity, not verdicts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {sortedCorrelations.slice(0, 5).map((correlation, index) => {
              const strength = Math.abs(correlation.correlationWithMood ?? 0);
              const isPositive = (correlation.correlationWithMood ?? 0) > 0;
              
              return (
                <div 
                  key={index}
                  className="p-4 rounded-lg border bg-card/50"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {isPositive ? (
                        <TrendingUp className="w-4 h-4 text-amber-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium">
                          {correlation.label}
                        </h4>
                        {strength > 0.4 && (
                          <Badge variant="secondary" className="text-xs">
                            Strong link
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {getCorrelationInsight(correlation.label, correlation.correlationWithMood)}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {correlation.sampleSize} days
                        </Badge>
                        {correlation.averageValue !== null && (
                          <Badge variant="outline" className="text-xs">
                            Avg: {correlation.averageValue.toFixed(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Empty / Low-data state */}
      {!isLoading && correlations.length === 0 && (
        <Card className="border-dashed border-primary/20">
          <CardContent className="py-8 text-center">
            <Brain className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <h3 className="text-sm font-medium mb-2">Patterns Are Still Forming</h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">
              Once you've logged a bit more data about your thoughts and mental load, 
              we'll be able to show you clearer patterns. Keep tracking your cognitive symptoms 
              alongside sleep, meditation, and food choices - your unique patterns will emerge.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Gentle Experiments Card */}
      <Card className="bg-gradient-to-br from-muted/30 to-muted/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-primary" />
            Gentle Experiments to Try
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>When mental fatigue is high, try simplifying tasks rather than pushing through</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Notice if a short meditation or EFT session shifts your thought patterns</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Journaling for 5 minutes can sometimes release repetitive thoughts</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Movement - even a short walk - often helps when concentration feels sticky</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Reassurance footer */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="py-4">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>Remember:</strong> Brain fog, rumination, and mental fatigue are common experiences 
            that fluctuate naturally. These patterns are <em>information</em>, not diagnoses or verdicts. 
            They're signals about what your nervous system might need - not evidence of failure. 
            If cognitive symptoms are severe or concerning, please speak with a healthcare provider.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
